import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Theme,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  faPhoneAlt,
  faTimes,
  faExclamationCircle,
  faEnvelope,
  faTruck,
  faQuestionCircle,
  faCalendarTimes,
  faShippingFast,
  faHeadset,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface CallInfoModalButtonProps {
  phoneNumber?: string;
  availableForCallHours?: string;
  buttonText?: string;
  emailAddress?: string; // Added email prop for use in text
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    paddingRight: theme.spacing(5), // Space for close button
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  listItemIcon: {},
  phoneTypography: {
    marginTop: theme.spacing(1), // Reduced top margin slightly
    textAlign: 'center',
    fontWeight: 'bold',
    color: theme.palette.secondary.main,
  },
  hoursTypography: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.secondary.main,
  },
  finalParagraph: {
    marginTop: theme.spacing(3),
    fontStyle: 'italic',
    textAlign: 'left',
  },
  buttonMargin: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  boldText: {
    fontWeight: 'bold',
  },
}));

export const CallInfoModalButton: React.FC<CallInfoModalButtonProps> = ({
  phoneNumber,
  availableForCallHours,
  buttonText = 'Zobrazit telefon',
  emailAddress = 'lekarna@magistrmartin.cz',
}) => {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const classes = useStyles();
  const theme = useTheme();

  const handleOpenInfoModal = () => setIsInfoModalOpen(true);
  const handleCloseInfoModal = () => setIsInfoModalOpen(false);

  return (
    <>
      {/* The Button that triggers the dialog */}
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={handleOpenInfoModal}
        startIcon={<FontAwesomeIcon icon={faPhoneAlt} />}
      >
        {buttonText}
      </Button>

      {/* The Dialog component */}
      <Dialog
        open={isInfoModalOpen}
        onClose={handleCloseInfoModal}
        aria-labelledby="info-dialog-title"
        scroll="paper"
        maxWidth="sm"
        fullWidth
      >
        {/* Dialog Title - Updated Text */}
        <DialogTitle id="info-dialog-title" className={classes.dialogTitle}>
          Jak Vám můžeme nejlépe pomoci?
          <IconButton aria-label="close" onClick={handleCloseInfoModal} className={classes.closeButton}>
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content - Updated Text */}
        <DialogContent dividers={true}>
          <DialogContentText component="div">
            Vážený kliente, chceme Vám pomoci co nejrychleji. Pro některé časté dotazy a požadavky existují efektivnější
            cesty než telefonát. Podívejte se, prosím, jak nejlépe postupovat:
          </DialogContentText>

          {/* --- List of reasons - Updated Text --- */}
          <List dense={true}>
            {/* Reklamace */}
            <ListItem>
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon={faEnvelope} color="orange" />
              </ListItemIcon>
              <ListItemText
                primary="Reklamace"
                secondary={`Pro co nejrychlejší vyřízení nám prosím napište email (${emailAddress}) s popisem situace, číslem objednávky, jménem a ideálně i fotkou. Email nám umožní vše důkladně prověřit a odpovědět Vám do cca 48 hod.`}
              />
            </ListItem>
            {/* Skladová dostupnost */}
            <ListItem>
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon={faQuestionCircle} color="blue" />
              </ListItemIcon>
              <ListItemText
                primary="Skladová dostupnost"
                secondary="Nejaktuálnější informaci naleznete vždy online u detailu produktu. Naši kolegové na telefonu mají k dispozici stejné údaje, nejrychleji to tedy ověříte přímo na webu."
              />
            </ListItem>
            {/* Expirace produktu */}
            <ListItem>
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon={faCalendarTimes} color="green" />
              </ListItemIcon>
              <ListItemText
                primary="Expirace produktu"
                secondary="Orientační expiraci uvádíme u produktu na webu. Přesnější datum bohužel nemůžeme telefonicky ověřit, protože zboží je již zabaleno."
              />
            </ListItem>
            {/* Doobjednávky */}
            <ListItem>
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon={faExclamationCircle} color="red" />
              </ListItemIcon>
              <ListItemText
                primary="Doobjednávky / Přidání k objednávce"
                secondary="Jakmile je objednávka odeslána, náš systém ji začne ihned zpracovávat a bohužel již do ní nelze přidávat další položky. Pokud jste něco zapomněli, nejrychlejším řešením je vytvořit novou objednávku."
              />
            </ListItem>
            {/* Expedice - Urychlení */}
            <ListItem>
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon={faShippingFast} color="purple" />
              </ListItemIcon>
              <ListItemText
                primary="Urychlení expedice"
                secondary="Chápeme, že na objednávku spěcháte. Všechny balíčky expedujeme co nejrychleji v pořadí, v jakém přišly. Individuální urychlení bohužel náš systém neumožňuje."
              />
            </ListItem>
            {/* Doprava */}
            <ListItem>
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon={faTruck} color="brown" />
              </ListItemIcon>
              <ListItemText
                primary="Problémy s dopravou"
                secondary="Nejrychlejší informace o stavu doručení získáte přímo od přepravní společnosti (kontakt najdete ve sledování zásilky). Pokud by problém přetrvával, napište nám prosím email, rádi Vám pomůžeme situaci dořešit."
              />
            </ListItem>
            {/* Poradenství */}
            <ListItem>
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon={faHeadset} color="teal" />
              </ListItemIcon>
              <ListItemText
                primary="Odborné poradenství"
                secondary="Pro Vaši bezpečnost a kvalitní radu poskytují naši lékárníci odborné poradenství pouze osobně v kamenné lékárně, kde mohou lépe posoudit Vaši situaci."
              />
            </ListItem>
            {/* Objednávání za někoho */}
            <ListItem>
              <ListItemIcon className={classes.listItemIcon}>
                <FontAwesomeIcon icon={faUsers} color="navy" />
              </ListItemIcon>
              <ListItemText
                primary="Objednávání pro jiné osoby"
                secondary="Telefonicky bohužel nemůžeme vytvořit objednávku za jinou osobu. Pokud potřebujete pomoci, zkuste prosím požádat příbuzné nebo známé o asistenci s online objednávkou."
              />
            </ListItem>
          </List>
          {/* --- End of list --- */}

          {/* Updated Text */}
          <DialogContentText component="div" style={{ marginTop: theme.spacing(2) }} className={classes.boldText}>
            Máte technické potíže s webem? Napište nám prosím email s popisem problému, pokusíme se pomoci nebo předáme
            na IT oddělení.
          </DialogContentText>

          {/* Updated Text */}
          <DialogContentText component="div" style={{ marginTop: theme.spacing(2) }}>
            Pokud jste zde nenašli odpověď nebo potřebujete řešit jinou záležitost, jsme tu pro Vás na telefonu v čase:
          </DialogContentText>

          {/* Display Hours */}
          {availableForCallHours && (
            <Typography variant="h6" component="p" className={classes.hoursTypography}>
              {availableForCallHours}
            </Typography>
          )}

          {/* Display Phone Number */}
          {phoneNumber && (
            <Typography
              variant="h5" // Keep number prominent
              component="p"
              className={classes.phoneTypography}
            >
              <FontAwesomeIcon icon={faPhoneAlt} style={{ marginRight: theme.spacing(1) }} />
              {phoneNumber}
            </Typography>
          )}

          {/* Updated Text */}
          <DialogContentText component="div" className={classes.finalParagraph}>
            Děkujeme, že nám pomáháte vyřizovat Vaše požadavky efektivně!
            <br />
            Váš tým lékárny
          </DialogContentText>
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions>
          <Button onClick={handleCloseInfoModal} color="primary">
            Zavřít
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
